// Niche context (plan §4.5): which verticals and cities are active, read from the rows rather
// than hard-coded. The shell chip, the lead filters and the map centre all come from here, so
// activating a second niche changes the UI without a deploy.

import { useQuery } from '@tanstack/react-query';
import { isConfigured, isDemoMode, supabase } from './supabase.js';
import { MOCK_CITIES, MOCK_VERTICALS } from './mockData-niche.js';

export interface NicheVertical {
  key: string;
  name_bg: string;
  active: boolean;
}

export interface NicheCity {
  key: string;
  name_bg: string;
  active: boolean;
  center_lat: number | null;
  center_lng: number | null;
}

export interface NicheContext {
  verticals: NicheVertical[];
  cities: NicheCity[];
  /** Only the active rows, in display order. */
  activeVerticals: NicheVertical[];
  activeCities: NicheCity[];
  /** "Бургас · Зъболекари"; several names joined when several are active. */
  label: string;
  /** Centre of the first active city, when it has one. */
  centre: [number, number] | null;
  /** Active or selected vertical for single-niche filtering. */
  selectedVertical?: NicheVertical | null | undefined;
  /** Active or selected city for single-territory filtering. */
  selectedCity?: NicheCity | null | undefined;
}

const EMPTY: NicheContext = {
  verticals: [],
  cities: [],
  activeVerticals: [],
  activeCities: [],
  label: '',
  centre: null,
  selectedVertical: null,
  selectedCity: null,
};

function build(verticals: NicheVertical[], cities: NicheCity[]): NicheContext {
  const activeVerticals = verticals.filter((v) => v.active);
  const activeCities = cities.filter((c) => c.active);
  const cityPart = activeCities.map((c) => c.name_bg).join(', ');
  const verticalPart = activeVerticals.map((v) => v.name_bg).join(', ');
  const label = [cityPart, verticalPart].filter((part) => part !== '').join(' · ');
  const first = activeCities[0];
  const centre: [number, number] | null =
    first !== undefined && first.center_lat !== null && first.center_lng !== null
      ? [first.center_lat, first.center_lng]
      : null;
  const selectedVertical = activeVerticals.length === 1 ? activeVerticals[0] : null;
  const selectedCity = activeCities.length === 1 ? activeCities[0] : null;
  return {
    verticals,
    cities,
    activeVerticals,
    activeCities,
    label,
    centre,
    selectedVertical,
    selectedCity,
  };
}

export function useNicheContext(): NicheContext {
  const { data } = useQuery({
    queryKey: ['niche_context'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<NicheContext> => {
      if (!isConfigured || isDemoMode) {
        return build(
          MOCK_VERTICALS.map(({ key, name_bg, active }) => ({ key, name_bg, active })),
          MOCK_CITIES.map(({ key, name_bg, active, center_lat, center_lng }) => ({
            key,
            name_bg,
            active,
            center_lat,
            center_lng,
          })),
        );
      }
      const [verticals, cities] = await Promise.all([
        supabase.from('verticals').select('key, name_bg, active').order('name_bg'),
        supabase
          .from('cities')
          .select('key, name_bg, active, center_lat, center_lng')
          .order('name_bg'),
      ]);
      if (verticals.error) throw new Error(verticals.error.message);
      if (cities.error) throw new Error(cities.error.message);
      return build(
        (verticals.data ?? []) as unknown as NicheVertical[],
        (cities.data ?? []) as unknown as NicheCity[],
      );
    },
  });
  return data ?? EMPTY;
}
